// Multi-stop gradient editor.
// - Renders the current stops as draggable circles on a colour ramp.
// - Click anywhere on the ramp to insert a stop.
// - Double-click a stop to remove it (min 2 stops kept).
// - Click a stop to open a colour picker.

import { el, on } from '@/lib/dom';
import { lerpRGBOklab } from '@/lib/oklab';
import { parseHex, type RGB } from '@/lib/color';
import type { Stop } from '@/lib/swatches';

export type StopsBarOpts = {
  stops: Stop[];
  onChange: (stops: Stop[]) => void;
  onBegin?: () => void;
  onEnd?: () => void;
};

export function stopsBar(opts: StopsBarOpts): HTMLElement {
  const ramp = el('div', { class: 'stops-ramp' });
  const handles = el('div', { class: 'stops-handles' });
  const root = el('div', { class: 'stops-bar' }, [ramp, handles]);

  let stops = sortStops([...opts.stops]);

  function commit(): void {
    opts.onChange([...stops]);
  }

  function repaint(): void {
    paintRamp(ramp, stops);
    paintHandles(handles, stops, {
      onColor: (i, color) => {
        stops[i] = { ...stops[i]!, color };
        repaint();
        commit();
      },
      onMove: (i, t) => {
        stops[i] = { ...stops[i]!, t };
        stops = sortStops(stops);
        repaint();
        commit();
      },
      onRemove: (i) => {
        if (stops.length <= 2) return;
        stops.splice(i, 1);
        repaint();
        commit();
      },
      onBegin: opts.onBegin,
      onEnd: opts.onEnd,
    });
  }

  // Click empty ramp area → add stop at clicked t.
  on(ramp, 'click', (ev) => {
    const rect = ramp.getBoundingClientRect();
    const t = clamp01((ev.clientX - rect.left) / rect.width);
    const color = sampleAt(stops, t);
    stops.push({ t, color });
    stops = sortStops(stops);
    repaint();
    commit();
  });

  repaint();
  return root;
}

function paintRamp(host: HTMLElement, stops: Stop[]): void {
  const sorted = sortStops(stops);
  const css = sorted
    .map((s) => `${s.color} ${(s.t * 100).toFixed(2)}%`)
    .join(', ');
  host.style.background = `linear-gradient(to right, ${css})`;
}

type HandleEvents = {
  onColor: (i: number, color: string) => void;
  onMove: (i: number, t: number) => void;
  onRemove: (i: number) => void;
  onBegin?: (() => void) | undefined;
  onEnd?: (() => void) | undefined;
};

function paintHandles(host: HTMLElement, stops: Stop[], events: HandleEvents): void {
  host.replaceChildren();
  // Build handles in original-order so onMove indices stay stable for caller.
  for (let i = 0; i < stops.length; i++) {
    const stop = stops[i]!;
    const handle = el('span', { class: 'stop-handle', title: stop.color }) as HTMLSpanElement;
    handle.style.left = `${stop.t * 100}%`;
    handle.style.background = stop.color;

    const colorInput = el('input', { type: 'color', value: stop.color }) as HTMLInputElement;
    colorInput.className = 'stop-color';
    on(colorInput, 'input', () => events.onColor(i, colorInput.value));
    on(colorInput, 'change', () => events.onEnd?.());
    on(colorInput, 'click', (ev) => ev.stopPropagation());
    handle.appendChild(colorInput);

    let dragging = false;
    on(handle, 'pointerdown', (ev) => {
      if ((ev as PointerEvent).button !== 0) return;
      ev.stopPropagation();
      // Prevent the colour picker from opening on a drag start.
      // We open it only on a clean click (no movement).
      const startX = (ev as PointerEvent).clientX;
      let moved = false;
      handle.setPointerCapture((ev as PointerEvent).pointerId);
      events.onBegin?.();

      const move = (mev: PointerEvent): void => {
        if (Math.abs(mev.clientX - startX) > 2) {
          dragging = true;
          moved = true;
        }
        if (dragging) {
          const rect = host.getBoundingClientRect();
          const t = clamp01((mev.clientX - rect.left) / rect.width);
          events.onMove(i, t);
        }
      };
      const up = (uev: PointerEvent): void => {
        handle.releasePointerCapture(uev.pointerId);
        handle.removeEventListener('pointermove', move);
        handle.removeEventListener('pointerup', up);
        events.onEnd?.();
        if (!moved) {
          // Treat as a click — open colour picker.
          colorInput.click();
        }
        dragging = false;
      };
      handle.addEventListener('pointermove', move);
      handle.addEventListener('pointerup', up);
    });
    on(handle, 'dblclick', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      events.onRemove(i);
    });

    host.appendChild(handle);
  }
}

function sortStops(stops: Stop[]): Stop[] {
  return [...stops].sort((a, b) => a.t - b.t);
}

function clamp01(n: number): number {
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

/** Sample what colour a new stop should be when inserted at t. */
function sampleAt(stops: Stop[], t: number): string {
  const sorted = sortStops(stops);
  if (sorted.length === 0) return '#888888';
  if (t <= sorted[0]!.t) return sorted[0]!.color;
  if (t >= sorted[sorted.length - 1]!.t) return sorted[sorted.length - 1]!.color;
  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i]!, b = sorted[i + 1]!;
    if (t >= a.t && t <= b.t) {
      const u = (t - a.t) / (b.t - a.t || 1);
      const ca = parseHex(a.color) ?? ([0, 0, 0] as RGB);
      const cb = parseHex(b.color) ?? ([255, 255, 255] as RGB);
      const out = lerpRGBOklab(ca, cb, u);
      return rgbToHex(out);
    }
  }
  return sorted[sorted.length - 1]!.color;
}

function rgbToHex(rgb: RGB): string {
  return '#' + rgb.map((c) => Math.max(0, Math.min(255, c | 0)).toString(16).padStart(2, '0')).join('');
}
